/**
 * Компонент для отображения свойств слоя сценария, сгруппированных по категориям (только просмотр)
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { LAYER_ATTRIBUTE_CATEGORIES } from '@/data/layerAttributeCategories';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { ScenarioLayerProperty } from '@/api/scenarioLayers';
import { formatAttributeLabel } from '@/utils/attributeLabel';

const PROPERTY_DECIMAL_PLACES = 3;

interface ScenarioLayerPropertiesByCategoryProps {
  properties: ScenarioLayerProperty[];
}

export const ScenarioLayerPropertiesByCategory: React.FC<ScenarioLayerPropertiesByCategoryProps> = ({
  properties,
}) => {
  // Создаем мапу свойств по имени атрибута для быстрого доступа
  const propertiesMap = new Map<string, ScenarioLayerProperty>();
  properties.forEach(prop => {
    if (isHiddenLayerAttribute(prop.attribute_id, prop.attribute?.name)) {
      return;
    }
    if (prop.attribute?.name) {
      propertiesMap.set(prop.attribute.name, prop);
    }
  });

  // Группируем свойства по категориям
  const categorizedProperties = LAYER_ATTRIBUTE_CATEGORIES.map(category => {
    const categoryProps = category.attributes
      .map(attrName => {
        const prop = propertiesMap.get(attrName);
        if (!prop) return null;

        return {
          ...prop,
          category: category,
        };
      })
      .filter((prop): prop is ScenarioLayerProperty & { category: typeof category } => prop !== null);

    return {
      category,
      properties: categoryProps,
    };
  }).filter(group => group.properties.length > 0);

  const getPropertyValue = (property: ScenarioLayerProperty): string => {
    if (property.string_value !== undefined && property.string_value !== null) {
      return String(property.string_value);
    }
    if (property.float_value !== undefined && property.float_value !== null) {
      return Number(property.float_value).toFixed(PROPERTY_DECIMAL_PLACES);
    }
    if (property.integer_value !== undefined && property.integer_value !== null) {
      return String(property.integer_value);
    }
    return '';
  };

  // Если нет категоризированных свойств, показываем сообщение
  if (categorizedProperties.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No properties available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {categorizedProperties.map(({ category, properties: categoryProps }) => (
        <Accordion key={category.id} defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${category.id}-content`}
            id={`${category.id}-header`}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              {category.name}
            </Typography>
            <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
              ({categoryProps.length} {categoryProps.length === 1 ? 'attribute' : 'attributes'})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {categoryProps.map((property) => (
                <Grid item xs={12} sm={6} key={property.id}>
                  <TextField
                    fullWidth
                    label={
                      property.attribute?.name
                        ? formatAttributeLabel(property.attribute.name, property.attribute.unit)
                        : `Property ${property.attribute_id.slice(0, 8)}`
                    }
                    value={getPropertyValue(property)}
                    size="small"
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
